import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  CloudDownload as CloudDownloadIcon,
  CloudUpload as CloudUploadIcon,
  Description as DescriptionIcon,
  Publish as PublishIcon,
} from "@material-ui/icons";
import { useRef, useState } from "react";
import { NitMasterAction } from "../../actions/NitMaster";
import { useSnackbar } from "../CommonComponents/SnackbarProvider";

import moment from "moment/moment";
import readXlsxFile from "read-excel-file";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    padding: theme.spacing(3),
    backgroundColor: "#003a6b",
  },
  title: {
    fontWeight: 600,
    color: "#fff",
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  fileUploadArea: {
    padding: theme.spacing(4, 2),
    textAlign: "center",
    border: `2px dashed #003a6b`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: "rgba(0, 58, 107, 0.03)",
    cursor: "pointer",
  },
  uploadIcon: {
    fontSize: 64,
    color: "#003a6b",
    marginBottom: theme.spacing(2),
  },
  uploadText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  selectedFile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2),
    backgroundColor: "rgba(46, 125, 50, 0.08)",
    borderRadius: theme.spacing(1),
    border: `1px solid rgba(46, 125, 50, 0.5)`,
    marginTop: theme.spacing(2),
  },
  fileInfo: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  fileIcon: {
    fontSize: 32,
    color: "#2e7d32",
  },
  fileName: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  instructions: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1.5),
    borderLeft: `4px solid #003a6b`,
    marginTop: theme.spacing(3),
  },
  instructionTitle: {
    fontWeight: 600,
    color: "#003a6b",
    marginBottom: theme.spacing(1),
  },
  instructionItem: {
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  requiredLabel: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  requiredAsterisk: {
    color: theme.palette.error.main,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  cancelButton: {
    color: theme.palette.text.secondary,
  },
  importButton: {
    background: "#003a6b",
    color: "#fff",
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    "&:hover": {
      background: "#002244",
    },
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(3),
    fontWeight: 800,
    fontSize: "1.1rem",
  },
  browseButton: {
    backgroundColor: "#003a6b",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#002244",
    },
  },
  statusCell: {
    padding: 10,
    fontWeight: "bold",
  },
  successStatus: {
    color: "#2e7d32",
  },
  warningStatus: {
    color: "#ed6c02",
  },
  errorStatus: {
    color: "#d32f2f",
  },
  pendingStatus: {
    color: "#757575",
  },
}));

const ExcelImportModalNIT = ({ open, onClose, onSuccess }) => {
  const { showSnackbar } = useSnackbar();
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [excelData, setExcelData] = useState([]);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      setError("Please select a valid Excel file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("File size should not exceed 10MB");
      return;
    }

    setSelectedFile(file);
    setError("");
    setExcelData([]);

    try {
      const data = await parseExcelFile(file);
      setExcelData(data);
    } catch (error) {
      console.error("Error parsing Excel file:", error);
      setError(
        error.message || "Failed to parse Excel file. Please check the format.",
      );
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const CONTRACT_TYPES = [
    { value: "OBR", label: "OBR" },
    { value: "COMPOSITE", label: "COMPOSITE" },
    { value: "L&T", label: "LT" },
    { value: "LT", label: "LT" },
  ];

  const WORK_TYPES = [
    { value: "OBR", label: "OBR" },
    { value: "L&T", label: "LT" },
    { value: "LT", label: "LT" },
  ];

  const parseExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      readXlsxFile(file)
        .then((rows) => {
          try {
            if (rows.length === 0) {
              reject(new Error("Excel file is empty"));
              return;
            }

            const headers = rows[0].map(
              (header) => header?.toString().toLowerCase().trim() || "",
            );

            const requiredColumns = {
              srNo: {
                keywords: ["sr"],
                name: "SR",
              },
              nitNumber: {
                keywords: ["nit no."],
                name: "NIT NO.",
              },
              commencementDate: {
                keywords: ["date of commencement"],
                name: "Date of commencement",
              },
              completionDate: {
                keywords: ["date of completion"],
                name: "Date of Completion",
              },
              typeOfContract: {
                keywords: ["type of contract"],
                name: "Type of Contract",
              },
              typeOfWork: {
                keywords: ["type of work"],
                name: "Type of Work",
              },
              mineName: {
                keywords: ["name of the ocp"],
                name: "Name of the OCP",
              },
              vendorName: {
                keywords: ["name of contractor"],
                name: "Name of contractor",
              },
            };

            const columnIndices = {};
            const missingColumns = [];

            Object.entries(requiredColumns).forEach(([key, config]) => {
              const index = headers.findIndex((header) =>
                config.keywords.some((keyword) => header.includes(keyword)),
              );

              if (index === -1) {
                missingColumns.push(config.name);
              } else {
                columnIndices[key] = index;
              }
            });

            if (missingColumns.length > 0) {
              reject(
                new Error(
                  `The uploaded file does not contain the required columns: ${missingColumns.join(
                    ", ",
                  )}. Please ensure the Excel file has the correct format.`,
                ),
              );
              return;
            }

            const processedData = rows
              .slice(1)
              .map((row, index) => {
                if (
                  !row ||
                  row.every(
                    (cell) =>
                      cell === null || cell === undefined || cell === "",
                  )
                ) {
                  return null;
                }

                const srNo = row[columnIndices.srNo]?.toString().trim() || "";
                const nitNumber =
                  row[columnIndices.nitNumber]?.toString().trim() || "";

                let commencementDate = row[columnIndices.commencementDate];
                let completionDate = row[columnIndices.completionDate];

                if (typeof commencementDate === "number") {
                  const excelEpoch = new Date(1900, 0, 1);
                  commencementDate = new Date(
                    excelEpoch.getTime() +
                      (commencementDate - 1) * 24 * 60 * 60 * 1000,
                  );
                }

                if (typeof completionDate === "number") {
                  const excelEpoch = new Date(1900, 0, 1);
                  completionDate = new Date(
                    excelEpoch.getTime() +
                      (completionDate - 1) * 24 * 60 * 60 * 1000,
                  );
                }

                const commencementDateStr =
                  commencementDate instanceof Date
                    ? commencementDate.toISOString().split("T")[0]
                    : commencementDate?.toString().trim() || "";

                const completionDateStr =
                  completionDate instanceof Date
                    ? completionDate.toISOString().split("T")[0]
                    : completionDate?.toString().trim() || "";

                const typeOfContract =
                  row[columnIndices.typeOfContract]?.toString().trim() || "";
                const typeOfWork =
                  row[columnIndices.typeOfWork]?.toString().trim() || "";
                const mineName =
                  row[columnIndices.mineName]?.toString().trim() || "";
                const vendorName =
                  row[columnIndices.vendorName]?.toString().trim() || "";
                if (!srNo) {
                  return {
                    id: index + 1,
                    srNo: "",
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,
                    status: "Missing SR",
                    isValid: false,
                  };
                }

                if (!nitNumber) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: "",
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,

                    status: "Missing NIT Number",
                    isValid: false,
                  };
                }

                if (!commencementDateStr) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: "",
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,

                    status: "Missing Commencement Date",
                    isValid: false,
                  };
                }

                if (!completionDateStr) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: "",
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,
                    status: "Missing Completion Date",
                    isValid: false,
                  };
                }
                if (!typeOfContract) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: "",
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,
                    status: "Missing Type of Contract",
                    isValid: false,
                  };
                }

                // Validate Type of Contract against CONTRACT_TYPES
                if (!CONTRACT_TYPES.some((ct) => ct.value === typeOfContract)) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,

                    status:
                      "Invalid Type of Contract (must be OBR, COMPOSITE, or LT)",
                    isValid: false,
                  };
                }

                if (!typeOfWork) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: "",

                    status: "Missing Type of Work",
                    isValid: false,
                  };
                }

                // Validate Type of Work against WORK_TYPES
                if (!WORK_TYPES.some((wt) => wt.value === typeOfWork)) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,

                    status: "Invalid Type of Work (must be OBR or LT)",
                    isValid: false,
                  };
                }

                // Add validation for mine name
                if (!mineName) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,
                    status: "Missing Mine Name",
                    isValid: false,
                  };
                }

                // Add validation for vendor name
                if (!vendorName) {
                  return {
                    id: index + 1,
                    srNo: srNo,
                    nitNumber: nitNumber,
                    commencementDate: commencementDateStr,
                    completionDate: completionDateStr,
                    typeOfContract: typeOfContract,
                    typeOfWork: typeOfWork,
                    mineName: mineName,
                    vendorName: vendorName,
                    status: "Missing Transporter Name",
                    isValid: false,
                  };
                }

                return {
                  id: index + 1,
                  srNo: srNo,
                  nitNumber: nitNumber,
                  commencementDate: commencementDateStr,
                  completionDate: completionDateStr,
                  typeOfContract: typeOfContract,
                  typeOfWork: typeOfWork,
                  mineName: mineName,
                  vendorName: vendorName,
                  status: "Pending",
                  isValid: true,
                };
              })
              .filter((row) => row !== null);

            if (processedData.length === 0) {
              reject(new Error("No valid data found in the Excel file"));
              return;
            }

            resolve(processedData);
          } catch (error) {
            reject(new Error("Error processing Excel data: " + error.message));
          }
        })
        .catch((error) => {
          reject(new Error("Failed to read Excel file: " + error.message));
        });
    });
  };

  const removeFile = () => {
    setSelectedFile(null);
    setExcelData([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError("Please select an Excel file");
      showSnackbar("Please select an Excel file", "error");
      return;
    }

    const invalidRows = excelData.filter((row) => !row.isValid);
    if (invalidRows.length > 0) {
      setError(
        `Found ${invalidRows.length} invalid row(s). Please fix them before importing.`,
      );
      showSnackbar(
        `Found ${invalidRows.length} invalid row(s) in the Excel file`,
        "error",
      );
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await NitMasterAction.importNITs(formData, showSnackbar);

      if (response?.responseCode === 200) {
        const updatedData = excelData.map((item) => {
          const found = response.data?.find(
            (d) => d.nitNumber === item.nitNumber,
          );
          return {
            ...item,
            status: found ? "Added Successfully" : "Already Exists",
          };
        });

        setExcelData(updatedData);

        showSnackbar(response.message, "success");

        if (onSuccess) {
          onSuccess();
        }
      } else {
        const errorMessage = response?.message;
        setError(errorMessage || "Failed to import NITs, check the file");
        showSnackbar(
          errorMessage || "Failed to import NITs, check the file",
          "error",
        );
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to import NITs";

      setError(errorMessage);
      showSnackbar(errorMessage, "error");

      console.error("Import error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      resetForm();
      onClose();
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setExcelData([]);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const viewFile = () => {
    if (selectedFile) {
      const fileUrl = URL.createObjectURL(selectedFile);
      window.open(fileUrl, "_blank");
    }
  };

  const getStatusClassName = (status) => {
    switch (status) {
      case "Added Successfully":
        return classes.successStatus;
      case "Already Exists":
        return classes.warningStatus;
      default:
        if (status?.includes("Invalid") || status?.includes("Missing")) {
          return classes.errorStatus;
        }
        return classes.pendingStatus;
    }
  };

  const EXCEL_TEMPLATE_URL = "/NIT_template.xlsx";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h5" className={classes.title}>
          Import NITs
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={isLoading}
          style={{ position: "absolute", right: 7, top: 16, color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography className={classes.requiredLabel}>
              Upload Excel File{" "}
              <span className={classes.requiredAsterisk}>*</span>
            </Typography>

            <div
              style={{
                margin: "10px 0",
              }}
            >
              <Link
                href={EXCEL_TEMPLATE_URL}
                download="NIT-import-template.xlsx"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#003a6b",
                  textDecoration: "none",
                  padding: "6px 12px",
                  backgroundColor: "rgba(0, 58, 107, 0.08)",
                  borderRadius: "4px",
                  border: "1px solid rgba(0, 58, 107, 0.2)",
                  fontSize: "14px",
                  fontWeight: 500,
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(0, 58, 107, 0.12)";
                  e.currentTarget.style.borderColor = "rgba(0, 58, 107, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(0, 58, 107, 0.08)";
                  e.currentTarget.style.borderColor = "rgba(0, 58, 107, 0.2)";
                }}
              >
                <CloudDownloadIcon fontSize="small" />
                Download Template to Import NITs
              </Link>
            </div>

            <Paper
              className={classes.fileUploadArea}
              onClick={() => !isLoading && fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                disabled={isLoading || selectedFile}
              />

              {!selectedFile ? (
                <>
                  <CloudUploadIcon className={classes.uploadIcon} />
                  <Typography variant="h6" gutterBottom>
                    Click to Upload
                  </Typography>
                  <Typography variant="body2" className={classes.uploadText}>
                    Supported formats: .xlsx, .xls, .csv (Max 10MB)
                  </Typography>
                  <Button
                    variant="contained"
                    className={classes.browseButton}
                    startIcon={<PublishIcon />}
                    disabled={isLoading}
                  >
                    Browse Files
                  </Button>
                </>
              ) : (
                <div className={classes.selectedFile}>
                  <div
                    className={classes.fileInfo}
                    onClick={viewFile}
                    style={{ cursor: "pointer" }}
                  >
                    <DescriptionIcon className={classes.fileIcon} />
                    <div>
                      <Typography
                        variant="subtitle1"
                        className={classes.fileName}
                      >
                        {selectedFile.name}
                      </Typography>
                    </div>
                  </div>
                  {!isLoading && (
                    <Button
                      className={classes.removeButton}
                      onClick={removeFile}
                      size="small"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              )}
            </Paper>

            {error && (
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <Typography variant="body2" className={classes.errorText}>
                  Error:
                </Typography>
                <Typography variant="body2" className={classes.errorText}>
                  {error}
                </Typography>
              </div>
            )}
          </Grid>

          {excelData.length > 0 && (
            <Grid item xs={12}>
              <Typography style={{ fontWeight: "bold", marginBottom: 10 }}>
                Data Preview ({excelData.length} records)
              </Typography>
              <Paper style={{ maxHeight: 500, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead
                    style={{
                      background: "#003a6b",
                      color: "white",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={{ padding: 10 }}>SR</th>
                      <th style={{ padding: 10 }}>NIT No.</th>
                      <th style={{ padding: 10 }}>Date of Commencement</th>
                      <th style={{ padding: 10 }}>Date of Completion</th>
                      <th style={{ padding: 10 }}>Type of Contract</th>
                      <th style={{ padding: 10 }}>Type of Work</th>
                      <th style={{ padding: 10 }}>Mine Name</th>
                      <th style={{ padding: 10 }}>Transporter Name</th>
                      <th style={{ padding: 10 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excelData.map((row, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: "1px solid #ddd" }}
                      >
                        <td style={{ padding: 10 }}>{row.srNo}</td>
                        <td style={{ padding: 10 }}>{row.nitNumber}</td>
                        <td style={{ padding: 10 }}>
                          {row.commencementDate
                            ? moment(row.commencementDate).format("DD-MM-YYYY")
                            : ""}
                        </td>
                        <td style={{ padding: 10 }}>
                          {row.completionDate
                            ? moment(row.completionDate).format("DD-MM-YYYY")
                            : ""}
                        </td>
                        <td style={{ padding: 10 }}>{row.typeOfContract}</td>
                        <td style={{ padding: 10 }}>{row.typeOfWork}</td>
                        <td style={{ padding: 10 }}>{row.mineName}</td>
                        <td style={{ padding: 10 }}>{row.vendorName}</td>
                        <td
                          className={`${classes.statusCell} ${getStatusClassName(row.status)}`}
                        >
                          {row.status || "Pending"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Paper>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          className={classes.cancelButton}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={isLoading || !selectedFile}
          className={classes.importButton}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CheckCircleIcon />
            )
          }
        >
          {isLoading ? "Importing..." : "Import NITs"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExcelImportModalNIT;
